import { ActionIcon, Box, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMenu2 } from '@tabler/icons-react';
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router';
import { useConversationsList } from '~portal/api';
import { AdminSettingsButton } from '~portal/components/AdminSettingsButton';
import { BrandLogo } from '~portal/components/BrandLogo';
import { ChatHistoryDrawer, ChatSidebar } from '~portal/components/chat-history';
import { ColorSchemeToggle } from '~portal/components/ColorSchemeToggle';
import { ChatAccessProvider } from '~portal/contexts';
import { touchTarget } from '~portal/styles/touch-target.css';
import * as styles from './route.css';

export const Route = createFileRoute('/_protected/chat')({
  component: ChatLayoutRoute,
});

function ChatLayoutRoute() {
  const params = useParams({ strict: false });
  const currentConversationId = 'conversationId' in params ? params.conversationId : undefined;

  return (
    <ChatAccessProvider conversationId={currentConversationId}>
      <ChatLayout />
    </ChatAccessProvider>
  );
}

function ChatLayout() {
  const params = useParams({ strict: false });
  const currentConversationId = 'conversationId' in params ? params.conversationId : undefined;
  const conversations = useConversationsList();

  // Get current conversation's summary
  const currentConversation = conversations.find((c) => c._id === currentConversationId);
  const conversationTitle = currentConversation?.summary ?? null;

  // Mobile navigation drawer state
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  return (
    <Group gap={0} align="stretch" wrap="nowrap" className={styles.chatLayout}>
      {/* Desktop sidebar — carries the brand mark, thread list and account block */}
      <ChatSidebar />

      <Stack gap={0} className={`${styles.chatColumn} ${styles.chatColumnFloatingChrome}`}>
        {/* Chat sub-header — mobile only; the desktop canvas is chromeless per the Figma frames */}
        <Group
          hiddenFrom="md"
          className={styles.subHeader}
          gap="sm"
          justify="space-between"
          wrap="nowrap"
          px="md"
        >
          <Group gap="sm" wrap="nowrap">
            {/* Neither build has any chrome above the chat (the platform's global nav stays out
                of the chat layout), so the sub-header carries the brand below md, where the
                sidebar (and its logo) collapses into the drawer */}
            <Box hiddenFrom="md" display="flex">
              <BrandLogo height={24} />
            </Box>
            {/* H2-214 — the ONE new-chat affordance below md is the drawer's labelled button.
                A circular "+" lived here as well, a thumb's width from the hamburger that opens
                the drawer holding the other one: two controls, same action, no way to tell them
                apart by looking. The drawer keeps it because that is where the thread list is —
                "start another one" belongs beside "the ones you already have" — and it is still
                two taps from anywhere in the app, the same as any other item in the history. */}
            <ActionIcon
              onClick={openDrawer}
              size="lg"
              variant="default"
              hiddenFrom="md"
              className={touchTarget}
              aria-label="Open chat history"
            >
              <IconMenu2 size={20} />
            </ActionIcon>
          </Group>

          {/* H2 mobile review — the sub-header carries the burger, the brand and the thread's
              title, and nothing else. The admin cog and the scheme control moved into the
              drawer: they are settings, they are used once a session, and on a 375px header
              they were two of the five things competing with the one thing that identifies
              the screen. */}
          {conversationTitle && (
            <Box flex={1} miw={0}>
              <Text size="sm" className={styles.conversationTitle}>
                {conversationTitle}
              </Text>
            </Box>
          )}
        </Group>

        <ChatHistoryDrawer opened={drawerOpened} onClose={closeDrawer} />

        {/* Padding is in the class, not a `p` prop — see `mainContent`. */}
        <Box className={styles.mainContent}>
          <Outlet />
        </Box>

        {/* The desktop chat floats its chrome over the plain canvas — admin entry and scheme
            control. Bottom-right, beside the composer rather than over the transcript: anything
            anchored to the top-right of a scrolling conversation ends up with message bubbles
            passing underneath it. The account widget sits in the sidebar's pinned footer.
            Standalone-only until the platform's top nav was replaced by the global nav, which
            the chat layout does not render — so the platform chat needs the same cluster. */}
        <Group visibleFrom="md" gap="sm" wrap="nowrap" className={styles.floatingChrome}>
          <AdminSettingsButton />
          <ColorSchemeToggle />
        </Group>
      </Stack>
    </Group>
  );
}
